import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbLoader2 } from "react-icons/tb";
import Popup from "src/components/Popup/Popup";
import "./examGroupEditor.scss";
import { apiRequest, ApiError } from "src/services/apiClient";
import { isDemoMode } from "src/demo/demoApi";
import { Exam } from "src/types";
import { useGoaCatalog } from "src/hooks/useGoaCatalog";
import { useJustifications } from "src/hooks/useJustifications";
import { GoaLineEditor } from "src/components/GoaLineEditor/GoaLineEditor";
import { GoaLineIssueBanners } from "src/components/GoaLineEditor/GoaLineIssueBanners";
import { useGoaLineIssues } from "src/components/GoaLineEditor/useGoaLineIssues";
import { toDraftRow, type GoaLineDraft, type MaterialLineDraft } from "src/components/GoaLineEditor/goaLineDraft";

const normalizeMaterialLines = (lines: MaterialLineDraft[]) =>
    JSON.stringify(lines.map(({ kind, label, cost, quantity, unitPrice, positionsnr }) => ({ kind, label, cost, quantity, unitPrice, positionsnr })));

export interface ConfirmPopupState {
    title: string;
    message: string;
    action: () => void;
    confirmLabel: string;
    confirmVariant: "primary" | "danger" | "write-off";
}

export function ExamGroupEditor({
    date,
    exams,
    invoiceId,
    manual = false,
    onClose,
    onSaved,
    onRequestConfirm,
}: {
    date: string;
    exams: Exam[];
    invoiceId: string;
    /** Invoice created by hand (chapter 18): its Einfachsatz is editable (codes outside the
     *  catalog), and saving needs no "differs from the imported file" confirmation — nothing was imported. */
    manual?: boolean;
    onClose: () => void;
    onSaved: () => void;
    onRequestConfirm: (popup: ConfirmPopupState) => void;
}) {
    const { t } = useTranslation();
    const [rows, setRows] = useState<GoaLineDraft[]>(() => exams.map(toDraftRow));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { catalogList, catalogByNr } = useGoaCatalog();
    const justifications = useJustifications();
    const issues = useGoaLineIssues(rows);

    const hasChanges = useMemo(() => {
        return rows.some((row) => {
            if (row._status !== "existing") return true;
            const original = exams.find((e) => e.id === row.id);
            if (!original) return true;
            return (
                row.code !== original.code ||
                row.description !== (original.description ?? "") ||
                row.quantity !== (original.quantity ?? 1) ||
                row.price !== (original.price ?? 0) ||
                row.factor !== (original.factor ?? 1) ||
                row.justification !== (original.justification ?? "") ||
                normalizeMaterialLines(row.materialLines) !==
                    normalizeMaterialLines((original.materialLines ?? []).map((ml) => ({ _localId: "", ...ml })))
            );
        });
    }, [rows, exams]);

    const formattedDate = date
        ? new Date(date).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
        : "";

    // A row is unchanged from its original if every editable field still matches — skip it
    // from the batch entirely rather than writing a no-op update.
    const rowChanged = (row: GoaLineDraft) => {
        const original = exams.find((e) => e.id === row.id);
        if (!original) return true;
        return (
            row.code !== original.code ||
            row.description !== (original.description ?? "") ||
            row.quantity !== (original.quantity ?? 1) ||
            row.price !== (original.price ?? 0) ||
            row.factor !== (original.factor ?? 1) ||
            row.justification !== (original.justification ?? "") ||
            normalizeMaterialLines(row.materialLines) !==
                normalizeMaterialLines((original.materialLines ?? []).map((ml) => ({ _localId: "", ...ml })))
        );
    };

    const performSave = async () => {
        setSaving(true);
        setError(null);
        try {
            if (isDemoMode()) {
                onSaved();
                return;
            }
            const asPayload = (row: GoaLineDraft) => ({
                code: row.code,
                description: row.description,
                quantity: row.quantity,
                price: row.price,
                factor: row.factor,
                justification: row.justification || undefined,
                materialLines: row.materialLines.map(({ kind, label, cost, quantity, unitPrice, positionsnr }) => ({ kind, label, cost, quantity, unitPrice, positionsnr })),
            });

            await apiRequest(
                "/exams/batch",
                {
                    method: "PUT",
                    body: JSON.stringify({
                        invoiceId,
                        date,
                        creates: rows.filter((r) => r._status === "new").map(asPayload),
                        updates: rows
                            .filter((r) => r._status === "existing" && r.id && rowChanged(r))
                            .map((r) => ({ id: r.id, ...asPayload(r) })),
                        deletes: rows.filter((r) => r._status === "deleted" && r.id).map((r) => r.id as string),
                    }),
                },
                { "Content-Type": "application/json" },
            );
            onSaved();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t("common.saveFailed"));
        } finally {
            setSaving(false);
        }
    };

    const handleSaveClick = () => {
        if (manual) {
            void performSave();
            return;
        }
        onRequestConfirm({
            title: t("invoicePage.padDivergenceTitle"),
            message: t("invoicePage.padDivergenceWarning"),
            confirmLabel: t("invoicePage.saveChanges"),
            confirmVariant: "danger",
            action: performSave,
        });
    };

    return (
        <Popup
            className="popup--wide"
            title={t("invoicePage.editServicesTitle", { date: formattedDate })}
            onClose={saving ? () => {} : onClose}
            message={
                <div className="exam-group-editor">
                    {saving && (
                        <div className="exam-group-editor__saving-overlay">
                            <TbLoader2 />
                            <span>{t("invoicePage.savingChangesMessage")}</span>
                        </div>
                    )}
                    <GoaLineIssueBanners issues={issues} />
                    <fieldset className="exam-group-editor__fieldset" disabled={saving}>
                        <GoaLineEditor
                            rows={rows}
                            onRowsChange={setRows}
                            catalogList={catalogList}
                            catalogByNr={catalogByNr}
                            duplicateCodes={issues.duplicateCodes}
                            priceEditable={manual}
                            justifications={justifications}
                        />
                    </fieldset>
                    {error && <p className="exam-group-editor__error">{error}</p>}
                </div>
            }
            actions={[
                { label: t("popup.cancel"), variant: "default", onClick: onClose, disabled: saving },
                {
                    label: saving ? t("invoicePage.saving") : t("invoicePage.saveChanges"),
                    variant: "primary",
                    disabled: saving || !hasChanges,
                    onClick: handleSaveClick,
                },
            ]}
        />
    );
}
